import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "../../hooks/useAuth0";
import api from "../../services/api";

// ── Stage definitions by amyloid type ──────────────────────────────────────
const STAGES = {
  "ATTR-CM": [
    { id: "diagnosis",   icon: "🔬", label: "Diagnosis",        desc: "ATTR-CM confirmed via biopsy or imaging" },
    { id: "staging",     icon: "📊", label: "Staging",          desc: "NT-proBNP & echocardiography staging" },
    { id: "treatment",   icon: "💊", label: "Treatment",        desc: "Stabilizer therapy — tafamidis or acoramidis" },
    { id: "trial",       icon: "🎯", label: "Clinical Trial",   desc: "Matched trials open for your profile" },
    { id: "monitoring",  icon: "📈", label: "Monitoring",       desc: "Biomarker tracking — NT-proBNP, echo" },
    { id: "outcome",     icon: "❤️", label: "Outcomes",         desc: "Quality of life & functional status" },
  ],
  "ATTRv": [
    { id: "genetic",     icon: "🧬", label: "Genetic Testing",  desc: "TTR variant confirmed — V122I, V30M, or other" },
    { id: "diagnosis",   icon: "🔬", label: "Diagnosis",        desc: "ATTRv confirmed with organ involvement" },
    { id: "counseling",  icon: "👨‍👩‍👧", label: "Family Screening", desc: "First-degree relatives should be tested" },
    { id: "treatment",   icon: "💊", label: "Treatment",        desc: "TTR silencer or stabilizer therapy" },
    { id: "trial",       icon: "🎯", label: "Clinical Trial",   desc: "Prevention & treatment trials available" },
    { id: "monitoring",  icon: "📈", label: "Monitoring",       desc: "Nerve conduction, cardiac echo, labs" },
  ],
  "AL": [
    { id: "diagnosis",   icon: "🔬", label: "Diagnosis",        desc: "Bone marrow biopsy — plasma cell clone" },
    { id: "staging",     icon: "📊", label: "Staging",          desc: "Mayo Stage I–IV — free light chain ratio" },
    { id: "treatment",   icon: "💊", label: "Treatment",        desc: "Chemotherapy + stem cell transplant" },
    { id: "response",    icon: "🧪", label: "Response Check",   desc: "dFLC levels — tracking treatment response" },
    { id: "trial",       icon: "🎯", label: "Clinical Trial",   desc: "CAR-T and novel AL-specific trials" },
    { id: "remission",   icon: "⭐", label: "Remission",        desc: "Hematologic complete response — organ recovery" },
  ],
  "AA": [
    { id: "underlying",  icon: "🔍", label: "Root Cause",       desc: "Identify underlying inflammatory condition" },
    { id: "diagnosis",   icon: "🔬", label: "Diagnosis",        desc: "AA confirmed — serum amyloid A protein" },
    { id: "treatment",   icon: "💊", label: "Treat Inflammation", desc: "Control underlying disease — SAA levels" },
    { id: "kidney",      icon: "🫘", label: "Kidney Protection", desc: "Nephrology care — CKD progression monitoring" },
    { id: "trial",       icon: "🎯", label: "Clinical Trial",   desc: "Anti-IL therapies and AA-specific trials" },
    { id: "monitoring",  icon: "📈", label: "Monitoring",       desc: "SAA levels, eGFR, proteinuria tracking" },
  ],
};

// ── Trial recommendations by type ──────────────────────────────────────────
const TRIAL_RECS = {
  "ATTR-CM": [
    { name: "ACT-EARLY",    nct: "NCT06563895", sponsor: "BridgeBio",    tag: "Prevention",   color: "#1A5EA8" },
    { name: "CLEOPATTRA",   nct: "NCT07207811", sponsor: "Novo Nordisk", tag: "Phase 3",      color: "#0ab5a0" },
    { name: "TRITON-CM",    nct: "NCT07052903", sponsor: "Alnylam",      tag: "Phase 3",      color: "#0B2545" },
    { name: "MAGNITUDE",    nct: "NCT06128629", sponsor: "Intellia",     tag: "Gene Editing", color: "#e05c3a" },
  ],
  "ATTRv": [
    { name: "ACT-EARLY",    nct: "NCT06563895", sponsor: "BridgeBio",    tag: "Prevention",   color: "#1A5EA8" },
    { name: "MaesTTRo",     nct: "NCT06465810", sponsor: "AstraZeneca",  tag: "Observational",color: "#0ab5a0" },
    { name: "TRACE-AI",     nct: "NCT07062848", sponsor: "Multi-site",   tag: "AI Screening", color: "#0B2545" },
    { name: "MAGNITUDE-2",  nct: "NCT06672237", sponsor: "Intellia",     tag: "Gene Editing", color: "#e05c3a" },
  ],
  "AL": [
    { name: "CARIN",        nct: "NCT04973137", sponsor: "Prothena",     tag: "Phase 3",      color: "#1A5EA8" },
    { name: "NEXICART-2",   nct: "NCT-pending", sponsor: "ImmixBio",     tag: "CAR-T",        color: "#0ab5a0" },
  ],
  "AA": [
    { name: "TRACE-AI",     nct: "NCT07062848", sponsor: "Multi-site",   tag: "AI Screening", color: "#1A5EA8" },
  ],
};

// ── Biomarker guidance ──────────────────────────────────────────────────────
const BIOMARKERS = {
  "ATTR-CM": [
    { name: "NT-proBNP",    normal: "< 125 pg/mL",   role: "Cardiac stress marker" },
    { name: "Troponin I",   normal: "< 0.04 ng/mL",  role: "Heart muscle damage" },
    { name: "eGFR",         normal: "> 60 mL/min",   role: "Kidney function" },
  ],
  "ATTRv": [
    { name: "NT-proBNP",    normal: "< 125 pg/mL",   role: "Cardiac involvement" },
    { name: "NIS score",    normal: "Lower is better", role: "Nerve damage assessment" },
    { name: "TTR levels",   normal: "See lab report", role: "Treatment response" },
  ],
  "AL": [
    { name: "dFLC",         normal: "< 40 mg/L",     role: "Treatment response key" },
    { name: "NT-proBNP",    normal: "< 125 pg/mL",   role: "Cardiac staging" },
    { name: "Free light chain", normal: "See ratio", role: "Disease activity" },
  ],
  "AA": [
    { name: "Serum Amyloid A", normal: "< 10 mg/L", role: "Disease activity marker" },
    { name: "eGFR",         normal: "> 60 mL/min",   role: "Kidney function" },
    { name: "Proteinuria",  normal: "< 150 mg/day",  role: "Kidney damage indicator" },
  ],
};

// ── Helper: detect amyloid type from profile ────────────────────────────────
function detectType(profile) {
  if (!profile) return "ATTR-CM";
  const t = (profile.amyloidType || profile.diagnosisType || "").toLowerCase();
  if (t.includes("al") || t.includes("light chain")) return "AL";
  if (t.includes("aa") || t.includes("serum amyloid a")) return "AA";
  if (t.includes("v122") || t.includes("hereditary") || t.includes("attrv") || t.includes("variant")) return "ATTRv";
  return "ATTR-CM";
}

// ── Helper: detect current stage ────────────────────────────────────────────
function detectCurrentStage(profile, type) {
  if (!profile) return 2; // default to treatment stage
  const hasTreatment = profile.currentMedications?.length > 0 || profile.currentTreatment;
  const hasTrialInterest = profile.trialInterest;
  if (hasTrialInterest) return 3;
  if (hasTreatment) return 3;
  return 1;
}

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,600;0,9..144,700;1,9..144,300&display=swap');

  .tpm-root { font-family: 'DM Sans', system-ui, sans-serif; }

  @keyframes tpmFadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes tpmPulse {
    0%,100% { box-shadow: 0 0 0 0 rgba(102,126,234,0.4); }
    50%      { box-shadow: 0 0 0 8px rgba(102,126,234,0); }
  }
  @keyframes tpmGlow {
    0%,100% { opacity: 0.6; }
    50%      { opacity: 1; }
  }
  @keyframes tpmSlideIn {
    from { opacity: 0; transform: translateX(-12px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes tpmProgress {
    from { width: 0%; }
    to   { width: var(--progress-width); }
  }

  .tpm-stage-node {
    transition: all 0.25s cubic-bezier(0.34,1.56,0.64,1);
    cursor: pointer;
  }
  .tpm-stage-node:hover { transform: scale(1.08) translateY(-2px); }
  .tpm-stage-node.active { animation: tpmPulse 2s infinite; }

  .tpm-trial-card {
    transition: all 0.2s ease;
    cursor: pointer;
  }
  .tpm-trial-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 32px rgba(0,0,0,0.12) !important;
  }

  .tpm-tab {
    transition: all 0.2s ease;
    cursor: pointer;
  }
  .tpm-tab:hover { background: rgba(102,126,234,0.1) !important; }
  .tpm-tab.active {
    background: linear-gradient(135deg, #1A5EA8, #0B2545) !important;
    color: white !important;
  }

  .tpm-copilot-btn {
    transition: all 0.2s ease;
  }
  .tpm-copilot-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(102,126,234,0.4) !important;
  }

  .tpm-progress-bar {
    animation: tpmProgress 1.2s ease 0.5s both;
  }
`;

export default function TreatmentPathwayMap() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth0();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStage, setActiveStage] = useState(null);
  const [activeTab, setActiveTab] = useState("pathway"); // pathway | trials | biomarkers
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get("/v1/profile");
        setProfile(res.data);
      } catch {
        // Use demo data for showcase
        setProfile({
          amyloidType: "ATTR-CM",
          currentTreatment: "Tafamidis 80mg",
          currentMedications: ["Tafamidis"],
          organInvolvement: ["Heart"],
          trialInterest: true,
          firstName: user?.name?.split(" ")[0] || "there",
        });
      } finally {
        setLoading(false);
        setTimeout(() => setAnimateIn(true), 100);
      }
    };
    load();
  }, [user]);

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400, flexDirection: "column", gap: 16 }}>
        <style>{STYLES}</style>
        <div style={{ width: 40, height: 40, borderRadius: "50%", border: "3px solid #e5e7eb", borderTopColor: "#1A5EA8", animation: "spin 0.8s linear infinite" }} />
        <p style={{ color: "#6b7280", fontSize: 14 }}>Building your pathway...</p>
      </div>
    );
  }

  const amyloidType = detectType(profile);
  const stages = STAGES[amyloidType] || STAGES["ATTR-CM"];
  const currentStageIdx = detectCurrentStage(profile, amyloidType);
  const trials = TRIAL_RECS[amyloidType] || [];
  const biomarkers = BIOMARKERS[amyloidType] || [];
  const progressPct = Math.round(((currentStageIdx + 1) / stages.length) * 100);
  const firstName = profile?.firstName || user?.name?.split(" ")[0] || "there";

  const typeColors = {
    "ATTR-CM": { primary: "#1A5EA8", secondary: "#0B2545", bg: "#EEF2FF", light: "#F5F3FF" },
    "ATTRv":   { primary: "#0ab5a0", secondary: "#0891b2", bg: "#ECFDF5", light: "#F0FDFA" },
    "AL":      { primary: "#e05c3a", secondary: "#d97706", bg: "#FEF2F2", light: "#FFFBEB" },
    "AA":      { primary: "#1B6CA8", secondary: "#0D2B45", bg: "#EBF5FF", light: "#F0F9FF" },
  };
  const tc = typeColors[amyloidType] || typeColors["ATTR-CM"];

  return (
    <div className="tpm-root" style={{ background: "#F8FAFF", minHeight: "100vh", paddingBottom: 80 }}>
      <style>{STYLES}</style>

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div style={{
        background: `linear-gradient(135deg, #0D2B45 0%, #1a3a5c 50%, #2d1b69 100%)`,
        padding: "40px 24px 80px",
        position: "relative", overflow: "hidden",
      }}>
        <div style={{ position: "absolute", inset: 0, opacity: 0.05,
          backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)",
          backgroundSize: "32px 32px" }} />

        <div style={{ maxWidth: 900, margin: "0 auto", position: "relative",
          animation: animateIn ? "tpmFadeUp 0.6s ease both" : "none" }}>

          {/* Type badge */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8,
            background: `${tc.primary}25`, border: `1px solid ${tc.primary}50`,
            borderRadius: 999, padding: "5px 14px", marginBottom: 16 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: tc.primary,
              animation: "tpmGlow 2s infinite" }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: tc.primary,
              letterSpacing: "0.1em", textTransform: "uppercase" }}>
              {amyloidType} Amyloidosis
            </span>
          </div>

          <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(24px,4vw,38px)",
            fontWeight: 700, color: "white", lineHeight: 1.2, margin: "0 0 10px" }}>
            Hi {firstName} — here's your pathway
          </h1>
          <p style={{ fontSize: 16, color: "rgba(255,255,255,0.6)", margin: "0 0 32px",
            fontWeight: 300, maxWidth: 560 }}>
            A personalized view of your treatment journey, open clinical trials, and what comes next.
          </p>

          {/* Progress bar */}
          <div style={{ background: "rgba(255,255,255,0.1)", borderRadius: 999,
            height: 8, maxWidth: 500, overflow: "hidden", marginBottom: 8 }}>
            <div className="tpm-progress-bar" style={{
              height: "100%", borderRadius: 999,
              background: `linear-gradient(90deg, ${tc.primary}, ${tc.secondary})`,
              "--progress-width": `${progressPct}%`,
            }} />
          </div>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", margin: 0 }}>
            Journey progress — {progressPct}% · Stage {currentStageIdx + 1} of {stages.length}
          </p>
        </div>
      </div>

      {/* ── Tab navigation ──────────────────────────────────────────────────── */}
      <div style={{ maxWidth: 900, margin: "-28px auto 0", padding: "0 20px",
        position: "relative", zIndex: 10 }}>
        <div style={{ background: "white", borderRadius: 16, padding: "6px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          display: "flex", gap: 4,
          animation: animateIn ? "tpmFadeUp 0.5s ease 0.1s both" : "none" }}>
          {[
            { id: "pathway",    label: "🗺️ Treatment Pathway" },
            { id: "trials",     label: "🎯 Matched Trials" },
            { id: "biomarkers", label: "🧪 Key Biomarkers" },
          ].map(tab => (
            <button key={tab.id} className={`tpm-tab ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1, padding: "10px 16px", border: "none", borderRadius: 12,
                fontSize: 13, fontWeight: 600, cursor: "pointer",
                background: activeTab === tab.id
                  ? `linear-gradient(135deg, ${tc.primary}, ${tc.secondary})`
                  : "transparent",
                color: activeTab === tab.id ? "white" : "#6b7280",
                fontFamily: "'DM Sans', system-ui, sans-serif",
                transition: "all 0.2s",
              }}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: "20px auto 0", padding: "0 20px" }}>

        {/* ── PATHWAY TAB ──────────────────────────────────────────────────── */}
        {activeTab === "pathway" && (
          <div style={{ animation: animateIn ? "tpmFadeUp 0.4s ease 0.2s both" : "none" }}>

            {/* Stage timeline */}
            <div style={{ background: "white", borderRadius: 20, padding: "32px 28px",
              boxShadow: "0 2px 16px rgba(0,0,0,0.06)", marginBottom: 20 }}>
              <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600,
                color: "#0D2B45", margin: "0 0 8px" }}>Your Treatment Journey</h2>
              <p style={{ fontSize: 13, color: "#9ca3af", margin: "0 0 32px" }}>
                Tap any stage to learn more about what happens at that point
              </p>

              {/* Stage nodes — horizontal scroll on mobile */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: 0,
                overflowX: "auto", paddingBottom: 8 }}>
                {stages.map((stage, i) => {
                  const isPast    = i < currentStageIdx;
                  const isCurrent = i === currentStageIdx;
                  const isFuture  = i > currentStageIdx;
                  const isActive  = activeStage === i;

                  return (
                    <div key={stage.id} style={{ display: "flex", alignItems: "flex-start",
                      flex: "1 1 0", minWidth: 100 }}>
                      {/* Node + label */}
                      <div style={{ display: "flex", flexDirection: "column",
                        alignItems: "center", width: "100%", gap: 12 }}>

                        {/* Connector line before */}
                        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                          <div style={{ flex: 1, height: 3, background: i === 0 ? "transparent"
                            : isPast || isCurrent ? `linear-gradient(90deg, ${tc.primary}, ${tc.secondary})`
                            : "#e5e7eb" }} />

                          {/* Node circle */}
                          <div className={`tpm-stage-node ${isCurrent ? "active" : ""}`}
                            onClick={() => setActiveStage(isActive ? null : i)}
                            style={{
                              width: isCurrent ? 52 : 44,
                              height: isCurrent ? 52 : 44,
                              borderRadius: "50%",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: isCurrent ? 22 : 18,
                              background: isPast
                                ? `linear-gradient(135deg, ${tc.primary}, ${tc.secondary})`
                                : isCurrent
                                  ? `linear-gradient(135deg, ${tc.primary}, ${tc.secondary})`
                                  : "white",
                              border: isCurrent
                                ? `3px solid ${tc.primary}`
                                : isPast ? "none" : "2px solid #e5e7eb",
                              boxShadow: isCurrent
                                ? `0 0 0 4px ${tc.primary}25, 0 4px 16px rgba(0,0,0,0.12)`
                                : isPast ? "0 2px 8px rgba(0,0,0,0.1)" : "none",
                              flexShrink: 0, zIndex: 2, position: "relative",
                              filter: isFuture ? "grayscale(0.3) opacity(0.6)" : "none",
                            }}>
                            {isPast ? "✓" : stage.icon}
                          </div>

                          <div style={{ flex: 1, height: 3, background: isCurrent
                            ? "#e5e7eb" : isPast
                            ? `linear-gradient(90deg, ${tc.primary}, ${tc.secondary})`
                            : "#e5e7eb" }} />
                        </div>

                        {/* Stage label */}
                        <div style={{ textAlign: "center", padding: "0 4px" }}>
                          <p style={{ fontSize: 11, fontWeight: isCurrent ? 700 : 500,
                            color: isCurrent ? tc.primary : isPast ? "#374151" : "#9ca3af",
                            margin: "0 0 2px", whiteSpace: "nowrap" }}>
                            {stage.label}
                          </p>
                          {isCurrent && (
                            <span style={{ fontSize: 9, fontWeight: 700, color: "white",
                              background: tc.primary, borderRadius: 999, padding: "2px 8px",
                              textTransform: "uppercase", letterSpacing: "0.06em" }}>
                              You are here
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Stage detail panel */}
              {activeStage !== null && (
                <div style={{ marginTop: 24, padding: "20px 24px",
                  background: tc.bg, border: `1px solid ${tc.primary}30`,
                  borderRadius: 14, animation: "tpmSlideIn 0.3s ease both" }}>
                  <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                    <div style={{ fontSize: 28, flexShrink: 0 }}>{stages[activeStage].icon}</div>
                    <div>
                      <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 600,
                        color: "#0D2B45", margin: "0 0 6px" }}>{stages[activeStage].label}</h3>
                      <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.6, margin: "0 0 16px" }}>
                        {stages[activeStage].desc}
                      </p>
                      <button className="tpm-copilot-btn"
                        onClick={() => {
                          window.dispatchEvent(new CustomEvent("copilot-preload", {
                            detail: { prompt: `Tell me more about the "${stages[activeStage].label}" stage of ${amyloidType} amyloidosis treatment` }
                          }));
                        }}
                        style={{ background: `linear-gradient(135deg, ${tc.primary}, ${tc.secondary})`,
                          color: "white", border: "none", borderRadius: 10, padding: "8px 20px",
                          fontSize: 13, fontWeight: 600, cursor: "pointer",
                          fontFamily: "'DM Sans', system-ui, sans-serif",
                          boxShadow: `0 4px 16px ${tc.primary}40`,
                          display: "inline-flex", alignItems: "center", gap: 8 }}>
                        🤝 Ask Care Copilot about this stage
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* What's next card */}
            <div style={{ background: `linear-gradient(135deg, ${tc.primary}15, ${tc.secondary}15)`,
              border: `1px solid ${tc.primary}30`, borderRadius: 16,
              padding: "24px 28px", display: "flex", gap: 20, alignItems: "flex-start" }}>
              <div style={{ fontSize: 32, flexShrink: 0 }}>🔭</div>
              <div>
                <p style={{ fontSize: 12, fontWeight: 700, color: tc.primary,
                  textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 6px" }}>
                  What's next for you
                </p>
                <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 600,
                  color: "#0D2B45", margin: "0 0 8px" }}>
                  {currentStageIdx < stages.length - 2
                    ? `Next step: ${stages[currentStageIdx + 1]?.label}`
                    : "Continue monitoring and maintain quality of life"}
                </h3>
                <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.6, margin: "0 0 16px" }}>
                  {currentStageIdx < stages.length - 2
                    ? stages[currentStageIdx + 1]?.desc
                    : "You're at an advanced stage of your journey. Keep your biomarkers tracked and stay connected with your care team."}
                </p>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button className="tpm-copilot-btn"
                    onClick={() => setActiveTab("trials")}
                    style={{ background: `linear-gradient(135deg, ${tc.primary}, ${tc.secondary})`,
                      color: "white", border: "none", borderRadius: 10, padding: "10px 20px",
                      fontSize: 13, fontWeight: 600, cursor: "pointer",
                      fontFamily: "'DM Sans', system-ui, sans-serif",
                      boxShadow: `0 4px 16px ${tc.primary}40` }}>
                    🎯 View My Matched Trials
                  </button>
                  <button className="tpm-copilot-btn"
                    onClick={() => navigate("/my-health/care-copilot")}
                    style={{ background: "white", color: tc.primary,
                      border: `1px solid ${tc.primary}40`, borderRadius: 10, padding: "10px 20px",
                      fontSize: 13, fontWeight: 600, cursor: "pointer",
                      fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                    🤝 Ask Care Copilot
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── TRIALS TAB ───────────────────────────────────────────────────── */}
        {activeTab === "trials" && (
          <div style={{ animation: animateIn ? "tpmFadeUp 0.4s ease 0.1s both" : "none" }}>

            <div style={{ background: "white", borderRadius: 20, padding: "28px",
              boxShadow: "0 2px 16px rgba(0,0,0,0.06)", marginBottom: 20 }}>
              <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600,
                color: "#0D2B45", margin: "0 0 6px" }}>
                Trials Open for {amyloidType} Patients
              </h2>
              <p style={{ fontSize: 13, color: "#9ca3af", margin: "0 0 24px" }}>
                These trials are actively recruiting patients with your amyloidosis type
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {trials.map((trial, i) => (
                  <div key={i} className="tpm-trial-card"
                    style={{ border: `1px solid #f3f4f6`, borderRadius: 14,
                      padding: "20px 24px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      background: "white",
                      borderLeft: `4px solid ${trial.color}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10,
                          marginBottom: 6 }}>
                          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 18,
                            fontWeight: 700, color: "#0D2B45", margin: 0 }}>
                            {trial.name}
                          </h3>
                          <span style={{ fontSize: 11, fontWeight: 700, color: "white",
                            background: trial.color, borderRadius: 999,
                            padding: "2px 10px", textTransform: "uppercase",
                            letterSpacing: "0.06em" }}>
                            {trial.tag}
                          </span>
                        </div>
                        <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>
                          Sponsored by {trial.sponsor} · {trial.nct}
                        </p>
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <a href={`https://clinicaltrials.gov/study/${trial.nct}`}
                          target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: 12, fontWeight: 600, color: trial.color,
                            textDecoration: "none", padding: "7px 14px",
                            border: `1px solid ${trial.color}40`, borderRadius: 8,
                            background: `${trial.color}10`, whiteSpace: "nowrap" }}>
                          View Trial →
                        </a>
                        <button
                          onClick={() => {
                            window.dispatchEvent(new CustomEvent("copilot-preload", {
                              detail: { prompt: `Tell me about the ${trial.name} clinical trial (${trial.nct}) — am I eligible and what does participation involve?` }
                            }));
                          }}
                          style={{ fontSize: 12, fontWeight: 600, color: "white",
                            padding: "7px 14px", border: "none", borderRadius: 8,
                            background: `linear-gradient(135deg, ${tc.primary}, ${tc.secondary})`,
                            cursor: "pointer",
                            fontFamily: "'DM Sans', system-ui, sans-serif",
                            whiteSpace: "nowrap" }}>
                          🤝 Ask Copilot
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* NLP search CTA */}
            <div style={{ background: `linear-gradient(135deg, #0D2B45, #1a3a5c)`,
              borderRadius: 16, padding: "28px 32px", textAlign: "center" }}>
              <p style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600,
                color: "white", margin: "0 0 8px" }}>
                Find more trials matched to your exact situation
              </p>
              <p style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", margin: "0 0 20px" }}>
                Describe your situation in plain English — our AI finds the right trials for you
              </p>
              <button onClick={() => navigate("/trials/nlp-search")}
                style={{ background: `linear-gradient(135deg, ${tc.primary}, ${tc.secondary})`,
                  color: "white", border: "none", borderRadius: 12, padding: "12px 28px",
                  fontSize: 14, fontWeight: 700, cursor: "pointer",
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  boxShadow: `0 4px 20px ${tc.primary}50` }}>
                ✨ Find My Trial — AI Search
              </button>
            </div>
          </div>
        )}

        {/* ── BIOMARKERS TAB ───────────────────────────────────────────────── */}
        {activeTab === "biomarkers" && (
          <div style={{ animation: animateIn ? "tpmFadeUp 0.4s ease 0.1s both" : "none" }}>

            <div style={{ background: "white", borderRadius: 20, padding: "28px",
              boxShadow: "0 2px 16px rgba(0,0,0,0.06)", marginBottom: 20 }}>
              <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600,
                color: "#0D2B45", margin: "0 0 6px" }}>
                Key Biomarkers to Track
              </h2>
              <p style={{ fontSize: 13, color: "#9ca3af", margin: "0 0 24px" }}>
                These lab values are most important for monitoring your {amyloidType} amyloidosis
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {biomarkers.map((bm, i) => (
                  <div key={i} style={{ border: "1px solid #f3f4f6", borderRadius: 14,
                    padding: "20px 24px", background: "#fafafa",
                    display: "flex", justifyContent: "space-between",
                    alignItems: "center", flexWrap: "wrap", gap: 16 }}>
                    <div>
                      <h3 style={{ fontSize: 16, fontWeight: 700, color: "#0D2B45",
                        margin: "0 0 4px" }}>{bm.name}</h3>
                      <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>{bm.role}</p>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <p style={{ fontSize: 11, fontWeight: 700, color: tc.primary,
                        textTransform: "uppercase", letterSpacing: "0.08em",
                        margin: "0 0 2px" }}>Normal Range</p>
                      <p style={{ fontSize: 14, fontWeight: 600, color: "#374151",
                        margin: 0 }}>{bm.normal}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Lab upload CTA */}
            <div style={{ background: `linear-gradient(135deg, ${tc.primary}15, ${tc.secondary}15)`,
              border: `1px solid ${tc.primary}30`, borderRadius: 16, padding: "24px 28px",
              display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <p style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 600,
                  color: "#0D2B45", margin: "0 0 6px" }}>
                  Upload your lab results
                </p>
                <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>
                  Care Copilot will interpret your values in plain English and flag anything that needs attention
                </p>
              </div>
              <button className="tpm-copilot-btn"
                onClick={() => {
                  window.dispatchEvent(new CustomEvent("copilot-preload", {
                    detail: { prompt: `I'd like to upload my lab results for interpretation` }
                  }));
                }}
                style={{ background: `linear-gradient(135deg, ${tc.primary}, ${tc.secondary})`,
                  color: "white", border: "none", borderRadius: 12, padding: "12px 24px",
                  fontSize: 14, fontWeight: 700, cursor: "pointer",
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  boxShadow: `0 4px 16px ${tc.primary}40`,
                  flexShrink: 0 }}>
                🧪 Upload Labs to Copilot
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}